import { ApproveReject } from "../ApproveReject";
import { useAsyncJobsContext, useToastsContext } from "@kaltura/mediaspace-shared-contexts";
import { postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { useState } from "react";
import { translatePlural } from "@kaltura/ds-react-utils";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import ErrorMessage from "../ErrorMessage";
import { Breakpoint } from "@mui/material";

interface ApproveResponse {
    success: boolean;
    errors?: { id: string; name: string }[];
}

export interface ApproveContainerProps {
    /**
     * callback for letting the app know media list should be refreshed
     */
    reloadMedia: () => void;

    /**
     * approve action url
     * ie kms-base-url/channelmoderation/index/approve-entries
     */
    url: string;

    /**
     * ids of current entries
     */
    entryIds: string[];

    /**
     * name of current entry
     */
    entryName: string;

    /**
     * if confirmation required before approval
     */
    requestConfirmation: boolean;

    /**
     * Keep the button large even if the button text is hidden
     * (according to the noTextBreakpoint prop).
     */
    forceLargeButton?: boolean;

    /**
     * The button will have no visible text under this breakpoint (default: "md").
     * Specify "xs" to never hide the text.
     */
    noTextBreakpoint?: Breakpoint;

    /**
     * threshold for creating bulk async actions for approving/rejecting media
     */
    bulkThreshold?: number;

    /**
     * analytics data
     */
    analytics: {
        action: string;
        buttonType: ButtonClickAnalyticsType;
    };

    /**
     * is the button disabled
     */
    disabled?: boolean;
}

export function ApproveContainer(props: ApproveContainerProps) {
    const { reloadMedia, url, entryIds, requestConfirmation, analytics, bulkThreshold, ...rest } = props;
    const [loading, setLoading] = useState(false);
    const { showToast } = useToastsContext();
    const sendButtonAnalytics = useButtonAnalytics();
    const { startPolling } = useAsyncJobsContext();
    const isBulkAsync = !!bulkThreshold && entryIds.length > bulkThreshold;

    let confirmationMessage = "";
    if (requestConfirmation) {
        confirmationMessage =
            entryIds.length > 1
                ? translate("Are you sure you want to approve %1 media?", [entryIds.length])
                : translate("Are you sure you want to approve “%1” ?", [props.entryName]);
    }
    if (isBulkAsync) {
        if (confirmationMessage) {
            confirmationMessage += "<br>";
        }
        confirmationMessage
            += translate(
                "This action may take a few minutes. During this time, you won’t be able to make additional changes to the media on this page. Do you wish to continue?"
            );
    }

    const sendAnalytics = (what: "act" | "act_confirm") => {
        switch (what) {
            case "act":
                // main action button
                sendButtonAnalytics(
                    analytics.action,
                    analytics.buttonType,
                    entryIds.length === 1 ? entryIds[0] : undefined
                );
                break;
            case "act_confirm":
                // action from confirmation
                sendButtonAnalytics(
                    "Confirmation box - Approve media",
                    analytics.buttonType,
                    entryIds.length === 1 ? entryIds[0] : undefined
                );
                break;
        }
    };

    /**
     * initiate a call to approve media and handle result
     */
    const approve = () => {
        setLoading(true);
        postKmsData(
            url,
            {
                entryIds: entryIds,
            }
        )
            .then(
                (response: ApproveResponse) => {
                    const itemsCount = entryIds.length;

                    if (response.success) {
                        if (isBulkAsync) {
                            // async job started: no toast, start polling
                            startPolling();
                        }
                        else {
                            const messageText = translatePlural(
                                "Media successfully approved",
                                "%1 Media successfully approved",
                                [itemsCount]
                            );

                            showToast({ severity: "success", message: messageText });
                        }
                    }
                    else if (response.errors) {
                        showToast({
                            severity: "error",
                            message: (
                                <ErrorMessage
                                    titleText={translate("Approval failed for the following items:")}
                                    errors={response.errors}
                                />
                            ),
                        });
                    }
                    else {
                        showToast({ severity: "error", message: translate("Some media could not be approved") });
                    }
                },
                (error) => {
                    showToast({ severity: "error", message: translate("Some media could not be approved") });
                }
            )
            .finally(() => {
                setLoading(false);
                // notify
                reloadMedia();
            });
    };

    return (
        <ApproveReject
            {...rest}
            action={"approve"}
            onClick={approve}
            loading={loading}
            requestConfirmation={requestConfirmation}
            confirmationMessage={confirmationMessage}
            isBulkAsync={isBulkAsync}
            report={sendAnalytics}
        />
    );
}

export default ApproveContainer;
